<div class="page-hero">
  <div>
    <p class="eyebrow">Fusion Connect</p>
    <h2>Live machine monitor</h2>
    <p class="sub">This page is for watching incoming reads from online machines. Those live reads are the source used to build saved daily stats and later reporting by machine, location, and sales rep.</p>
  </div>
  <div class="hero-actions d-flex gap-2 flex-wrap">
    <button class="btn btn-light shadow-sm" (click)="refreshAll()">Refresh live data</button>
    <button class="btn btn-outline-light shadow-sm" (click)="openStatsWorkspace()">Open stats view</button>
  </div>
</div>

<div class="row g-3 mb-4">
  <div class="col-md-3">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Machines reporting</div>
      <div class="stat-value">{{ liveBoardCount }}</div>
      <div class="stat-help">boards with recent live reads</div>
    </div>
  </div>
  <div class="col-md-3">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Locations online</div>
      <div class="stat-value">{{ liveLocationCount }}</div>
      <div class="stat-help">locations in the current live feed</div>
    </div>
  </div>
  <div class="col-md-3">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Saved daily rows</div>
      <div class="stat-value">{{ statsPage.totalElements }}</div>
      <div class="stat-help">completed daily stats in this date window</div>
    </div>
  </div>
  <div class="col-md-3">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Last feed update</div>
      <div class="stat-value small-value">{{ lastReadTimestamp ? (lastReadTimestamp | date:'MM/dd HH:mm') : 'No data' }}</div>
      <div class="stat-help">last refresh {{ lastRefreshAt | date:'shortTime' }}</div>
    </div>
  </div>
</div>

<div class="row g-4 mb-4">
  <div class="col-lg-7">
    <div class="card shadow-sm h-100">
      <div class="card-header">
        <div class="eyebrow text-uppercase">Purpose</div>
        <h5 class="mb-0">How this should work</h5>
      </div>
      <div class="card-body">
        <div class="process-step">
          <div class="step-number">1</div>
          <div>
            <div class="fw-semibold">Machines send live hourly reads</div>
            <div class="text-muted small">Use the table below to confirm which boards are online and reporting.</div>
          </div>
        </div>
        <div class="process-step">
          <div class="step-number">2</div>
          <div>
            <div class="fw-semibold">Reads are converted into stats</div>
            <div class="text-muted small">The saved daily totals become the basis for daily, weekly, monthly, and yearly reporting.</div>
          </div>
        </div>
        <div class="process-step mb-0">
          <div class="step-number">3</div>
          <div>
            <div class="fw-semibold">Review by machine, location, or sales rep</div>
            <div class="text-muted small">Use the new stats page to analyze completed stats and current-day progress.</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="col-lg-5">
    <div class="card shadow-sm h-100">
      <div class="card-header d-flex align-items-center justify-content-between">
        <div>
          <div class="eyebrow text-uppercase">Saved stats</div>
          <h5 class="mb-0">Completed daily window</h5>
        </div>
        <button class="btn btn-outline-primary btn-sm" [disabled]="loadingStats" (click)="refreshStats()">
          {{ loadingStats ? 'Loading…' : 'Reload' }}
        </button>
      </div>
      <div class="card-body">
        <form [formGroup]="statsForm" class="row g-3">
          <div class="col-md-6">
            <label class="form-label">From</label>
            <input class="form-control" type="date" formControlName="from" />
          </div>
          <div class="col-md-6">
            <label class="form-label">To</label>
            <input class="form-control" type="date" formControlName="to" />
          </div>
          <div class="col-12">
            <label class="form-label">Machine filter</label>
            <select class="form-select" formControlName="boardId">
              <option value="">All boards</option>
              <option *ngFor="let b of boards; trackBy: trackById" [value]="b.id">
                {{ b.assetTag || b.serialNumber || b.id }}
              </option>
            </select>
          </div>
          <div class="col-12 d-flex justify-content-between align-items-center">
            <span class="text-muted small">Daily stats are saved to the database and used for reporting.</span>
            <button type="button" class="btn btn-primary" [disabled]="loadingStats || statsForm.invalid" (click)="loadDailyStats(0)">Apply</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>

<div class="card shadow-sm mb-4">
  <div class="card-header d-flex justify-content-between align-items-center">
    <div>
      <div class="eyebrow text-uppercase">Advanced</div>
      <h5 class="mb-0">Optional ingest tools</h5>
    </div>
    <button class="btn btn-outline-secondary btn-sm" (click)="toggleIngestTools()">
      {{ showIngestTools ? 'Hide tools' : 'Show tools' }}
    </button>
  </div>
  <div class="card-body" *ngIf="showIngestTools">
    <p class="text-muted small">This section is only for manual testing. It is not required for normal monitoring.</p>
    <form [formGroup]="readForm" class="row g-3 align-items-end">
      <div class="col-md-4">
        <label class="form-label">Board</label>
        <select class="form-select" formControlName="boardId">
          <option value="">Select a Fusion Connect board</option>
          <option *ngFor="let b of boards; trackBy: trackById" [value]="b.id">
            {{ b.assetTag || b.serialNumber || b.id }}
          </option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="form-label">Coin In</label>
        <input class="form-control" type="number" formControlName="coinIn" min="0" />
      </div>
      <div class="col-md-2">
        <label class="form-label">Coin Out</label>
        <input class="form-control" type="number" formControlName="coinOut" min="0" />
      </div>
      <div class="col-md-2">
        <label class="form-label">Cash In</label>
        <input class="form-control" type="number" formControlName="cashIn" min="0" />
      </div>
      <div class="col-md-2">
        <label class="form-label">Cash Out</label>
        <input class="form-control" type="number" formControlName="cashOut" min="0" />
      </div>
      <div class="col-md-2">
        <label class="form-label">Games</label>
        <input class="form-control" type="number" formControlName="gamesPlayed" min="0" />
      </div>
      <div class="col-md-2">
        <label class="form-label">Wins</label>
        <input class="form-control" type="number" formControlName="gamesWon" min="0" />
      </div>
      <div class="col-md-4">
        <label class="form-label">Timestamp</label>
        <input class="form-control" type="datetime-local" formControlName="timeStamp" />
      </div>
      <div class="col-md-4 d-flex gap-2">
        <button type="button" class="btn btn-light" (click)="resetTimestamp()">Now</button>
        <button type="button" class="btn btn-primary" [disabled]="readForm.invalid || submitting" (click)="submitRead()">
          {{ submitting ? 'Sending…' : 'Send test read' }}
        </button>
      </div>
    </form>
  </div>
</div>

<div class="card shadow-sm mb-4">
  <div class="card-header d-flex justify-content-between align-items-center">
    <div>
      <div class="eyebrow text-uppercase">Manual board stat</div>
      <h5 class="mb-0">Create regular board stats from selected live reads</h5>
    </div>
  </div>
  <div class="card-body border-bottom">
    <form [formGroup]="manualStatForm" class="row g-3 align-items-end">
      <div class="col-lg-3 col-md-6">
        <label class="form-label">Machine</label>
        <select class="form-select" formControlName="boardId">
          <option value="">Select board</option>
          <option *ngFor="let b of boards; trackBy: trackById" [value]="b.id">
            {{ b.assetTag || b.serialNumber || b.id }}
          </option>
        </select>
      </div>
      <div class="col-lg-3 col-md-6">
        <label class="form-label">From date/time</label>
        <input class="form-control" type="datetime-local" formControlName="fromDateTime" />
      </div>
      <div class="col-lg-3 col-md-6">
        <label class="form-label">To date/time</label>
        <input class="form-control" type="datetime-local" formControlName="toDateTime" />
      </div>
      <div class="col-lg-3 col-md-6 d-flex gap-2 justify-content-end">
        <button
          type="button"
          class="btn btn-outline-primary"
          [disabled]="loadingManualReads || manualStatForm.invalid"
          (click)="loadManualStatReads()"
        >
          {{ loadingManualReads ? 'Loading reads…' : 'Load reads' }}
        </button>
        <button
          type="button"
          class="btn btn-success"
          [disabled]="creatingManualStat || selectedManualStartIndex === null || selectedManualEndIndex === null"
          (click)="createBoardStatFromSelectedReads()"
        >
          {{ creatingManualStat ? 'Creating…' : 'Create stat row' }}
        </button>
      </div>
    </form>
  </div>

  <div class="table-responsive" *ngIf="manualReads.length > 0">
    <table class="table align-middle mb-0">
      <thead>
        <tr>
          <th>Start</th>
          <th>End</th>
          <th>Timestamp</th>
          <th class="text-end">Cash In</th>
          <th class="text-end">Cash Out</th>
          <th class="text-end">Coin In</th>
          <th class="text-end">Coin Out</th>
          <th class="text-end">Games Played</th>
          <th class="text-end">Games Won</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let r of manualReads; let i = index; trackBy: trackById">
          <td>
            <input
              type="radio"
              name="manualStartRead"
              [checked]="selectedManualStartIndex === i"
              (change)="selectManualStart(i)"
              [attr.aria-label]="'Select start read ' + formatReadKey(r)"
            />
          </td>
          <td>
            <input
              type="radio"
              name="manualEndRead"
              [checked]="selectedManualEndIndex === i"
              (change)="selectManualEnd(i)"
              [attr.aria-label]="'Select end read ' + formatReadKey(r)"
            />
          </td>
          <td>{{ r.timeStamp | date:'yyyy-MM-dd HH:mm' }}</td>
          <td class="text-end">{{ r.cashIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.cashOut | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.coinIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.coinOut | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.gamesPlayed | number }}</td>
          <td class="text-end">{{ r.gamesWon | number }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="card-footer" *ngIf="manualReads.length > 0">
    <span class="text-muted small">
      Pick one start read and one end read. The new board stat row will use end minus start values and save to the regular board stats table.
    </span>
  </div>
</div>

<div class="card shadow-sm mb-4">
  <div class="card-header d-flex justify-content-between align-items-center">
    <div>
      <div class="eyebrow text-uppercase">Feed</div>
      <h5 class="mb-0">Live reads</h5>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-outline-primary" [disabled]="readsPage.page === 0 || loadingReads" (click)="prevReadsPage()">Prev</button>
      <button class="btn btn-outline-primary" [disabled]="readsPage.page + 1 >= readsPage.totalPages || loadingReads" (click)="nextReadsPage()">Next</button>
    </div>
  </div>
  <div class="card-body border-bottom">
    <form [formGroup]="readsFilters" class="row g-3 align-items-end">
      <div class="col-md-5">
        <label class="form-label">Board filter</label>
        <select class="form-select" formControlName="boardId">
          <option value="">All boards</option>
          <option *ngFor="let b of boards; trackBy: trackById" [value]="b.id">
            {{ b.assetTag || b.serialNumber || b.id }} · {{ b.location?.locationName || 'No location' }}
          </option>
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label">Page size</label>
        <input class="form-control" type="number" min="10" max="500" formControlName="pageSize" />
      </div>
      <div class="col-md-4 d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-outline-secondary" (click)="readsFilters.reset({ boardId: '', pageSize: readsPage.size })">Reset</button>
        <button type="button" class="btn btn-primary" [disabled]="loadingReads" (click)="loadReads(0)">Apply</button>
      </div>
    </form>
  </div>
  <div class="table-responsive">
    <table class="table align-middle mb-0">
      <thead>
        <tr>
          <th>Period</th>
          <th>Location</th>
          <th>Machine</th>
          <th>Game</th>
          <th class="text-end">Cash In</th>
          <th class="text-end">Cash Out</th>
          <th class="text-end">Gross</th>
          <th class="text-end">Coin In</th>
          <th class="text-end">Coin Out</th>
          <th class="text-end">Games Played</th>
          <th class="text-end">Games Won</th>
          <th class="text-end">Reads</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngIf="!loadingReads && reads?.length === 0">
          <td colspan="12" class="text-center text-muted py-4">No reads yet.</td>
        </tr>
        <tr *ngFor="let r of reads; trackBy: trackById">
          <td>{{ r.timeStamp | date:'yyyy-MM-dd HH:mm' }}</td>
          <td>{{ r.location?.locationName || r.board?.location?.locationName || r.board?.currentDeployment?.location?.locationName || '—' }}</td>
          <td>
            <div class="fw-semibold">
              {{ r.board?.assetTag || r.board?.serialNumber || '—' }}
            </div>
            <div class="text-muted small">
              {{ r.board?.serialNumber || ' ' }}
            </div>
          </td>
          <td>
            <div class="fw-semibold">{{ getReadGameName(r) }}</div>
            <div class="text-muted small" *ngIf="getReadGameVersion(r)">
              {{ getReadGameVersion(r) }}
            </div>
          </td>
          <td class="text-end">{{ r.cashIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.cashOut | number:'1.0-2' }}</td>
          <td class="text-end fw-semibold">{{ (r.cashIn || 0) - (r.cashOut || 0) | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.coinIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.coinOut | number:'1.0-2' }}</td>
          <td class="text-end">{{ r.gamesPlayed | number }}</td>
          <td class="text-end">{{ r.gamesWon | number }}</td>
          <td class="text-end">1</td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="card-footer d-flex justify-content-between align-items-center">
    <span class="text-muted small">Page {{ readsPage.page + 1 }} of {{ readsPage.totalPages || 1 }} • {{ readsPage.totalElements }} total</span>
    <div class="d-flex gap-2">
      <button class="btn btn-outline-secondary btn-sm" [disabled]="readsPage.page === 0 || loadingReads" (click)="prevReadsPage()">Previous</button>
      <button class="btn btn-outline-secondary btn-sm" [disabled]="readsPage.page + 1 >= readsPage.totalPages || loadingReads" (click)="nextReadsPage()">Next</button>
    </div>
  </div>
</div>

<div class="card shadow-sm">
  <div class="card-header d-flex justify-content-between align-items-center">
    <div>
      <div class="eyebrow text-uppercase">Daily</div>
      <h5 class="mb-0">Live stats rollup</h5>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-outline-primary" [disabled]="statsPage.page === 0 || loadingStats" (click)="prevStatsPage()">Prev</button>
      <button class="btn btn-outline-primary" [disabled]="statsPage.page + 1 >= statsPage.totalPages || loadingStats" (click)="nextStatsPage()">Next</button>
    </div>
  </div>
  <div class="table-responsive">
    <table class="table align-middle mb-0">
      <thead>
        <tr>
          <th>Period</th>
          <th>Location</th>
          <th>Machine</th>
          <th class="text-end">Cash In</th>
          <th class="text-end">Cash Out</th>
          <th class="text-end">Gross</th>
          <th class="text-end">Coin In</th>
          <th class="text-end">Coin Out</th>
          <th class="text-end">Games Played</th>
          <th class="text-end">Games Won</th>
          <th class="text-end">Reads</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngIf="!loadingStats && stats?.length === 0">
          <td colspan="11" class="text-center text-muted py-4">No stats for this window.</td>
        </tr>
        <tr *ngFor="let s of stats; trackBy: trackById">
          <td>{{ s.statDate }}</td>
          <td>{{ s.locationName || '—' }}</td>
          <td>
            <div class="fw-semibold">{{ s.assetTag || s.serialNumber || s.boardId }}</div>
          </td>
          <td class="text-end">{{ s.cashIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ s.cashOut | number:'1.0-2' }}</td>
          <td class="text-end fw-semibold">{{ ((s.cashIn || 0) - (s.cashOut || 0)) | number:'1.0-2' }}</td>
          <td class="text-end">{{ s.coinIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ s.coinOut | number:'1.0-2' }}</td>
          <td class="text-end">{{ s.gamesPlayed | number }}</td>
          <td class="text-end">{{ s.gamesWon | number }}</td>
          <td class="text-end">{{ s.reads || 0 }}</td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="card-footer d-flex justify-content-between align-items-center">
    <span class="text-muted small">Page {{ statsPage.page + 1 }} of {{ statsPage.totalPages || 1 }} • {{ statsPage.totalElements }} total</span>
    <div class="d-flex gap-2">
      <button class="btn btn-outline-secondary btn-sm" [disabled]="statsPage.page === 0 || loadingStats" (click)="prevStatsPage()">Previous</button>
      <button class="btn btn-outline-secondary btn-sm" [disabled]="statsPage.page + 1 >= statsPage.totalPages || loadingStats" (click)="nextStatsPage()">Next</button>
    </div>
  </div>
</div>
